import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TravelExplore } from '@mui/icons-material';
import { Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { useAppSelector } from '~/modules/hooks';
import { selectUser } from '~/selectors';
import ReactGA from 'react-ga4';

import { getSearchQueryString, offerSearchDateFormat } from '~/modules/helpers';

import DateRangeSelector from '~/components/Form/DateRangeSelector';
import LocationSelector from '~/components/Form/LocationSelector';
import TravelersOptionsSelector from '~/components/Form/TravelersOptionsSelector';

import { FlightSearchFormValidationMessages, FlightSearchParams, FlightSearchRequestParams, Place, RootState } from '~/types';
import { Currency, FlightType, TripTravellers } from '~/types/common';
import { useDispatch, useSelector } from 'react-redux';
import { resetBookingData, resetOfferSearchData, setUserIdentityPreference, updateBookingContext } from '~/actions';
import { BOOKING_CONTEXT, NAVIGATOR_ROUTES, USER_IDENTITY_PREFERENCE } from '~/literals';

export interface FlightSearchProps {
  searchParams?: FlightSearchParams;
  hideSearchButton?: boolean;
  bookingContext?: string;
}

function FlightSearch(props: FlightSearchProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hideSearchButton, searchParams: searchParamsToSet } = props;
  const [validationMode, setValidationMode] = useState(false);
  const currency = useSelector<RootState, Currency>(({ app }) => app.currency);

  const [searchParams, setSearchParams] = useState<FlightSearchParams>(
    searchParamsToSet ?? {
      from: null,
      to: null,
      type: 'return',
      travelClass: 'ECONOMY',
      passengers: {
        adults: 1,
        children: 0,
        infants: 0,
      },
      departureDate: null,
      returnDate: null,
      currencyCode: currency,
    },
  );

  const user = useAppSelector(selectUser);
  const { isAuthenticated } = user;

  const [validationMessages, setValidationMessages] = useState<FlightSearchFormValidationMessages>({
    to: [],
    from: [],
    departure: [],
    return: [],
  });

  useEffect(() => {
    dispatch(updateBookingContext(props.bookingContext || ''));
  }, []);

  useEffect(() => {
    const newSearchParams = {
      ...searchParams,
      currencyCode: currency,
    };
    setSearchParams(newSearchParams);
  }, [currency]);

  const handleFlightTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchParams = {
      ...searchParams,
      type: event.target.value as FlightType,
    };

    setSearchParams(newSearchParams);
  };

  const handleTravelClassChange = (value: string) => {
    const newSearchParams = {
      ...searchParams,
      travelClass: value,
    };

    setSearchParams(newSearchParams);
  };

  const handleAdultPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    const newSearchParams = {
      ...searchParams,
      passengers: {
        ...searchParams.passengers,
        adults: value,
      } as TripTravellers,
    };

    setSearchParams(newSearchParams);
  };

  const handleChildrenPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    setSearchParams(previousSearchParams => ({
      ...previousSearchParams,
      passengers: {
        ...previousSearchParams.passengers,
        children: value,
      } as TripTravellers,
    }));
  };

  const handleInfantPassangersChange = (
    _:
      | React.FocusEvent<HTMLInputElement, Element>
      | React.PointerEvent<Element>
      | React.KeyboardEvent<Element>,
    value: number | null,
  ) => {
    const newSearchParams = {
      ...searchParams,
      passengers: {
        ...searchParams.passengers,
        infants: value,
      } as TripTravellers,
    };

    setSearchParams(newSearchParams);
  };

  const handleFromLocationChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: Place | null | string,
  ) => {
    const newSearchParams = {
      ...searchParams,
      from: value,
    } as FlightSearchParams;

    setSearchParams(newSearchParams);
    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleToLocationChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: Place | null | string,
  ) => {
    const newSearchParams = {
      ...searchParams,
      to: value,
    } as FlightSearchParams;

    setSearchParams(newSearchParams);
    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleDepartureDateChange = (date: Date | null) => {
    let newSearchParams = {
      ...searchParams,
      departureDate: date,
    } as FlightSearchParams;

    if (date !== null) {
      setSearchParams(newSearchParams);
    }

    if (date && searchParams.returnDate && date > searchParams.returnDate) {
      newSearchParams = {
        ...searchParams,
        returnDate: date,
      };
      setSearchParams(newSearchParams);
    }

    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleReturnDateChange = (date: Date | null) => {
    const newSearchParams = {
      ...searchParams,
      returnDate: date,
    } as FlightSearchParams;

    if (date !== null) {
      setSearchParams(newSearchParams);
    }

    setValidationMessages(getSearchValidationMessages(newSearchParams));
  };

  const handleSearchSubmitted = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const messages = getSearchValidationMessages(searchParams);

    setValidationMessages(messages);

    if (
      messages.to.length ||
      messages.from.length ||
      messages.departure.length ||
      (searchParams.type === 'return' && messages.return.length)
    ) {
      setValidationMode(true);
    } else {
      setValidationMode(false);
      const requestParams: FlightSearchRequestParams = {
        adults: searchParams.passengers.adults,
        children: searchParams.passengers.children,
        infants: searchParams.passengers.infants,
        travelClass: searchParams.travelClass,
        flightType: searchParams.type.toString(),
        from: searchParams.from?.iata as string,
        to: searchParams.to?.iata as string,
        departureDate: searchParams.departureDate ? offerSearchDateFormat(searchParams.departureDate) : '',
        currencyCode: searchParams.currencyCode,
        bookingContext: props.bookingContext || BOOKING_CONTEXT.FLIGHT,
      };
      if (searchParams.returnDate) {
        requestParams.returnDate = offerSearchDateFormat(searchParams.returnDate);
      }
      
      // Track flight/itinerary search
      const isItinerarySearch = props.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY;
      ReactGA.event({
        category: 'FlightSearch',
        action: isItinerarySearch ? 'Tripenger Itinerary Search Submitted' : 'Tripenger Flight Search Submitted',
        label: `${searchParams.from?.iata}-${searchParams.to?.iata}`,
        value: searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants,
      });
      
      const queryString = getSearchQueryString(requestParams, 10);
      dispatch(resetBookingData());
      dispatch(resetOfferSearchData());
      if (!isAuthenticated) {
        dispatch(setUserIdentityPreference(USER_IDENTITY_PREFERENCE.NOT_SELECTED));
      }
      navigate(`/flights?${queryString}`, { state: { from: NAVIGATOR_ROUTES.SearchWidget } });
    }
  };

  // Mobile responsiveness using theme and media query
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));  // Detect mobile devices
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));  // Detect mobile devices

  return (
    <Box component="form" noValidate onSubmit={handleSearchSubmitted}>
      <Grid container spacing={1}>
        <Grid item xs={12} className="top-options">
          <TravelersOptionsSelector
            onAdultPassangersChange={handleAdultPassangersChange}
            onChildrenPassangersChange={handleChildrenPassangersChange}
            onFlightTypeChange={handleFlightTypeChange}
            onInfantPassangersChange={handleInfantPassangersChange}
            onTravelClassChange={handleTravelClassChange}
            tripOptions={{
              type: searchParams.type,
              passengers: searchParams.passengers,
              travelClass: searchParams.travelClass,
            }}
          />
        </Grid>

        {/* Location Selectors (Side by Side for Mobile) */}
        <Grid item xs={isMedium ? 12 : (searchParams.type === 'oneway' ? 6 : 5)}>
          <Grid container spacing={2}>
            <Grid item xs={isSmall ? 12 : 6}>
              <LocationSelector
                errorHelperText={validationMode ? validationMessages.from : undefined}
                label="From"
                location={searchParams.from}
                name="from"
                onLocationChange={handleFromLocationChange}
                placeholder="From"
                required
              />
            </Grid>
            <Grid item xs={isSmall ? 12 : 6}>
              <LocationSelector
                errorHelperText={validationMode ? validationMessages.to : undefined}
                label="To"
                location={searchParams.to}
                name="to"
                onLocationChange={handleToLocationChange}
                placeholder="To"
                required
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Date Range Selector */}
        <Grid item xs={isMedium ? 12 : (searchParams.type === 'oneway' ? 4 : 5)} className="trip-date-range-selector">
          <DateRangeSelector
            dateRange={{
              from: searchParams.departureDate,
              to: searchParams.returnDate,
            }}
            fromDateRequired
            fromHelperText={validationMode ? validationMessages.departure : undefined}
            fromLabel={searchParams.type === 'oneway' ? '(One Way) Departure *' : 'Departure *'}
            onFromDateChange={handleDepartureDateChange}
            onToDateChange={handleReturnDateChange}
            singleDateMode={searchParams.type === 'oneway'}
            toDateRequired={searchParams.type === 'return'}
            toHelperText={validationMode ? validationMessages.return : undefined}
            toLabel="Return *"
          />
        </Grid>

        {/* Search Button (Full Width for Mobile) */}
        <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {hideSearchButton ? null : (
            <Button
              fullWidth
              startIcon={<TravelExplore />}
              sx={{ maxHeight: 56 }}
              type="submit"
              variant="contained"
            >
              Search
            </Button>
          )}
        </Grid>

      </Grid>
    </Box>
  );
}

export default FlightSearch;

const getSearchValidationMessages = (
  fields: FlightSearchParams,
): FlightSearchFormValidationMessages => {
  const validationMessages: FlightSearchFormValidationMessages = {
    to: [],
    from: [],
    departure: [],
    return: [],
  };

  if (!fields.from) {
    validationMessages.from.push('* Please select where to travel from.');
  }

  if (!fields.to) {
    validationMessages.to.push('* Please select where to travel to.');
  }

  if (fields.from && fields.from === fields.to) {
    validationMessages.to.push("* Origin and destination can't be the same.");
  }

  if (!fields.departureDate) {
    validationMessages.departure.push('* Please select departure date.');
  }

  if (!fields.returnDate) {
    validationMessages.return.push('* Please select return date.');
  }

  if (fields.returnDate && fields.departureDate && fields.departureDate > fields.returnDate) {
    validationMessages.return.push("* Return date can't be before departure date.");
  }

  return validationMessages;
};
